import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTheme } from './ThemeContext';

const ColorSchemeContext = createContext();

export const useColorScheme = () => {
  const context = useContext(ColorSchemeContext);
  if (!context) {
    throw new Error('useColorScheme must be used within a ColorSchemeProvider');
  }
  return context;
};

export const ColorSchemeProvider = ({ children }) => {
  const { colorScheme, theme, mode } = useTheme();
  const [forceUpdate, setForceUpdate] = useState(0);
  
  useEffect(() => {
    const handleColorSchemeChange = () => {
      setForceUpdate(prev => prev + 1);
    };
    
    const handleThemeChange = () => {
      setForceUpdate(prev => prev + 1);
    };
    
    window.addEventListener('colorSchemeChanged', handleColorSchemeChange);
    window.addEventListener('themeChanged', handleThemeChange);
    
    return () => {
      window.removeEventListener('colorSchemeChanged', handleColorSchemeChange);
      window.removeEventListener('themeChanged', handleThemeChange);
    };
  }, []);
  
  const getColorClasses = (type) => {
    // Use CSS variables for theme-aware backgrounds
    const getCardBackground = () => {
      return 'theme-card-bg';
    };

    const colorMap = {
      gold: {
        // Accent colors - use CSS variables
        primary: 'from-yellow-400 to-amber-500',  // Keep for compatibility but not used
        primaryHover: 'from-yellow-500 to-amber-600',
        border: 'border-[var(--color-primary)]',
        text: 'text-[var(--color-primary)]',
        bg: 'bg-[var(--color-primary)]',
        bgHover: 'hover:bg-[var(--color-primary)]',
        // Theme colors
        background: 'bg-transparent',
        cardBackground: getCardBackground(),
        textPrimary: 'theme-text-title',
        textSecondary: 'theme-text-body',
        textMuted: 'theme-text-muted',
        borderColor: 'theme-border',
        // Additional dynamic colors
        listBullet: 'text-[var(--color-primary)]',
        hoverAccent: 'hover:text-[var(--color-primary)]',
        borderAccent: 'border-[var(--color-primary)]',
        bgAccent: 'bg-[var(--color-primary)]'
      },
      blue: {
        // Accent colors - use CSS variables
        primary: 'from-blue-400 to-blue-600',  // Keep for compatibility but not used
        primaryHover: 'from-blue-500 to-blue-700',
        border: 'border-[var(--color-primary)]',
        text: 'text-[var(--color-primary)]',
        bg: 'bg-[var(--color-primary)]',
        bgHover: 'hover:bg-[var(--color-primary)]',
        // Theme colors
        background: 'bg-transparent',
        cardBackground: getCardBackground(),
        textPrimary: 'theme-text-title',
        textSecondary: 'theme-text-body',
        textMuted: 'theme-text-muted',
        borderColor: 'theme-border',
        // Additional dynamic colors
        listBullet: 'text-[var(--color-primary)]',
        hoverAccent: 'hover:text-[var(--color-primary)]',
        borderAccent: 'border-[var(--color-primary)]',
        bgAccent: 'bg-[var(--color-primary)]'
      },
      purple: {
        // Accent colors - use CSS variables
        primary: 'from-purple-400 to-purple-600',  // Keep for compatibility but not used
        primaryHover: 'from-purple-500 to-purple-700',
        border: 'border-[var(--color-primary)]',
        text: 'text-[var(--color-primary)]',
        bg: 'bg-[var(--color-primary)]',
        bgHover: 'hover:bg-[var(--color-primary)]',
        // Theme colors
        background: 'bg-transparent',
        cardBackground: getCardBackground(),
        textPrimary: 'theme-text-title',
        textSecondary: 'theme-text-body',
        textMuted: 'theme-text-muted',
        borderColor: 'theme-border',
        // Additional dynamic colors
        listBullet: 'text-[var(--color-primary)]',
        hoverAccent: 'hover:text-[var(--color-primary)]',
        borderAccent: 'border-[var(--color-primary)]',
        bgAccent: 'bg-[var(--color-primary)]'
      },
      green: {
        // Accent colors - use CSS variables
        primary: 'from-green-400 to-green-600',  // Keep for compatibility but not used
        primaryHover: 'from-green-500 to-green-700',
        border: 'border-[var(--color-primary)]',
        text: 'text-[var(--color-primary)]',
        bg: 'bg-[var(--color-primary)]',
        bgHover: 'hover:bg-[var(--color-primary)]',
        // Theme colors
        background: 'bg-transparent',
        cardBackground: getCardBackground(),
        textPrimary: 'theme-text-title',
        textSecondary: 'theme-text-body',
        textMuted: 'theme-text-muted',
        borderColor: 'theme-border',
        // Additional dynamic colors
        listBullet: 'text-[var(--color-primary)]',
        hoverAccent: 'hover:text-[var(--color-primary)]',
        borderAccent: 'border-[var(--color-primary)]',
        bgAccent: 'bg-[var(--color-primary)]'
      },
      red: {
        // Accent colors - use CSS variables
        primary: 'from-red-400 to-red-600',  // Keep for compatibility but not used
        primaryHover: 'from-red-500 to-red-700',
        border: 'border-[var(--color-primary)]',
        text: 'text-[var(--color-primary)]',
        bg: 'bg-[var(--color-primary)]',
        bgHover: 'hover:bg-[var(--color-primary)]',
        // Theme colors
        background: 'bg-transparent',
        cardBackground: getCardBackground(),
        textPrimary: 'theme-text-title',
        textSecondary: 'theme-text-body',
        textMuted: 'theme-text-muted',
        borderColor: 'theme-border',
        // Additional dynamic colors
        listBullet: 'text-[var(--color-primary)]',
        hoverAccent: 'hover:text-[var(--color-primary)]',
        borderAccent: 'border-[var(--color-primary)]',
        bgAccent: 'bg-[var(--color-primary)]'
      }
    };
    
    return colorMap[colorScheme] || colorMap.gold;
  };

  const value = {
    colorScheme,
    theme,
    mode,
    getColorClasses,
    forceUpdate,
    key: forceUpdate  // Add key to force re-renders
  };

  return (
    <ColorSchemeContext.Provider value={value}>
      {children}
    </ColorSchemeContext.Provider>
  );
};
